import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  User, Mail, Phone, Shield, Edit3, Save, X, Users,
  AlertCircle, Loader2,
} from 'lucide-react';

const GUARDIAN_LABELS = { parent: 'Parent', guardian: 'Guardian', grandparent: 'Grandparent', other: 'Other' };

const initials = (fn, ln) => `${fn?.[0] || ''}${ln?.[0] || ''}`.toUpperCase();

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const ymd = typeof dateStr === 'string' ? dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
    const d = ymd
      ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
      : new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
};

const formatGrade = (grade) => {
  if (!grade || grade === 'K') return 'Kindergarten';
  return `Grade ${grade}`;
};

export default function ProfilePage() {
  const { user, api, refreshUser } = useAuth();
  const [learners, setLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    guardian_type: '',
    photo_consent: false,
    sms_consent: false,
    marketing_consent: false,
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        guardian_type: user.guardian_type || '',
        photo_consent: user.photo_consent || false,
        sms_consent: user.sms_consent || false,
        marketing_consent: user.marketing_consent || false,
      });
    }
  }, [user]);

  useEffect(() => {
    api.get('/learners/')
      .then(res => setLearners(res.data?.results || res.data || []))
      .catch(() => {})
      .finally(() => setLoadingLearners(false));
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/`, form);
      await refreshUser();
      toast.success('Profile updated');
      setEditing(false);
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      guardian_type: user.guardian_type || '',
      photo_consent: user.photo_consent || false,
      sms_consent: user.sms_consent || false,
      marketing_consent: user.marketing_consent || false,
    });
    setEditing(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">My Profile</h1>
        <p className="text-muted-foreground">Manage your account information and learner details.</p>
      </div>

      {/* Parent Profile Card */}
      <Card className="card-dashboard">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-5 w-5" /> Account Information
          </CardTitle>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit3 className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar + identity */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-xl">{initials(user?.first_name, user?.last_name)}</span>
            </div>
            <div>
              <p className="font-semibold text-lg">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-muted-foreground">{GUARDIAN_LABELS[user?.guardian_type] || 'Parent'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '—'}
              </p>
            </div>
          </div>

          <Separator />

          {/* Editable fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">First Name</Label>
              {editing ? (
                <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              ) : (
                <p className="text-sm font-medium">{user?.first_name || '—'}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Last Name</Label>
              {editing ? (
                <Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
              ) : (
                <p className="text-sm font-medium">{user?.last_name || '—'}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email
              </Label>
              <p className="text-sm font-medium">{user?.email || '—'}</p>
              <p className="text-[10px] text-muted-foreground">Contact support to change your email</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Phone className="h-3 w-3" /> Phone
              </Label>
              {editing ? (
                <Input
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{user?.phone || '—'}</p>
                  {user?.phone_verified && (
                    <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">Verified</Badge>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Guardian Type</Label>
              {editing ? (
                <Select value={form.guardian_type} onValueChange={v => setForm(f => ({ ...f, guardian_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="guardian">Guardian</SelectItem>
                    <SelectItem value="grandparent">Grandparent</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium">{GUARDIAN_LABELS[user?.guardian_type] || '—'}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Consents */}
          <div>
            <p className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4" /> Preferences & Consents
            </p>
            <div className="space-y-2">
              {[
                { key: 'photo_consent', label: 'Photo Consent', desc: 'Allow photos of learners for marketing materials' },
                { key: 'sms_consent', label: 'SMS Notifications', desc: 'Receive text message updates about sessions' },
                { key: 'marketing_consent', label: 'Marketing Emails', desc: 'Receive promotions and APS news via email' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/40">
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                  {editing ? (
                    <Switch checked={form[key]} onCheckedChange={v => setForm(f => ({ ...f, [key]: v }))} />
                  ) : (
                    <Badge variant={user?.[key] ? 'default' : 'outline'} className="text-[10px]">
                      {user?.[key] ? 'Enabled' : 'Disabled'}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Learners Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2">
            <Users className="h-5 w-5" /> My Learners
          </h2>
          <Link to="/parent/learners">
            <Button variant="outline" size="sm">Manage Learners →</Button>
          </Link>
        </div>

        {loadingLearners ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : learners.length === 0 ? (
          <Card className="card-dashboard">
            <CardContent className="py-10 text-center">
              <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-3">No learners added yet</p>
              <Link to="/parent/learners"><Button size="sm">Add Your First Learner</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {learners.map(learner => (
              <Card key={learner.id} className="card-dashboard">
                <CardContent className="p-5 space-y-4">
                  {/* Learner header */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-sm">
                        {initials(learner.first_name, learner.last_name)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{learner.first_name} {learner.last_name}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <Badge variant="secondary" className="text-[10px]">{formatGrade(learner.grade)}</Badge>
                        {learner.photo_consent && (
                          <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">Photo OK</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Date of Birth</p>
                      <p className="text-xs font-medium">{formatDate(learner.date_of_birth)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">T-Shirt Size</p>
                      <p className="text-xs font-medium">{learner.tshirt_size_display || learner.tshirt_size || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">School</p>
                      <p className="text-xs font-medium">{learner.school_name || '—'}</p>
                      {learner.district_name && (
                        <p className="text-[10px] text-muted-foreground">{learner.district_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  {(learner.emergency_contact_name || learner.emergency_contact_phone) && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                      <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Emergency Contact
                      </p>
                      <p className="text-xs font-medium">{learner.emergency_contact_name || '—'}</p>
                      {learner.emergency_contact_phone && (
                        <p className="text-xs text-muted-foreground">{learner.emergency_contact_phone}</p>
                      )}
                    </div>
                  )}

                  {/* Health & Accessibility */}
                  {(learner.allergies || learner.medical_notes || learner.accessibility_notes) && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 space-y-2">
                      <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Health & Accessibility
                      </p>
                      {learner.allergies && (
                        <div>
                          <p className="text-[10px] text-muted-foreground">Allergies</p>
                          <p className="text-xs">{learner.allergies}</p>
                        </div>
                      )}
                      {learner.medical_notes && (
                        <div>
                          <p className="text-[10px] text-muted-foreground">Medical Notes</p>
                          <p className="text-xs">{learner.medical_notes}</p>
                        </div>
                      )}
                      {learner.accessibility_notes && (
                        <div>
                          <p className="text-[10px] text-muted-foreground">Accessibility</p>
                          <p className="text-xs">{learner.accessibility_notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pickup Authorization */}
                  {Array.isArray(learner.pickup_authorization) && learner.pickup_authorization.length > 0 && (
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wide mb-1.5">
                        Authorized Pickup
                      </p>
                      <div className="space-y-1">
                        {learner.pickup_authorization.map((person, idx) => (
                          <p key={idx} className="text-xs">
                            {typeof person === 'string' ? person : person.name}
                            {person.phone ? ` · ${person.phone}` : ''}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
